import { yup } from '../initYup.ts';
import { type YupAnyObject } from '../YupAnyObject.ts';
import { ParentDepsKey } from './parentWhen.ts';

declare module 'yup' {
  interface ObjectSchema<
    TIn extends yup.Maybe<yup.AnyObject>,
    TContext = yup.AnyObject,
    TDefault = any,
    TFlags extends yup.Flags = '',
  > {
    /**
     * Consolida le dipendenze `parentWhen` dei figli diretti portandole
     * nelle `deps` dello schema corrente. Ogni `^` iniziale viene
     * strippato perché il livello è già salito di uno.
     *
     * Le deps che dopo lo strip hanno ancora il prefisso `^` vengono
     * anche scritte in `meta[ParentDepsKey]`, così che il livello
     * superiore (es. un ArraySchema via `addChildrenParentDeps`, o
     * `createRepeatableSchema`) possa propagarle ulteriormente.
     */
    addChildrenParentDeps(): this;
  }

  interface ArraySchema<TIn, TContext, TDefault, TFlags> {
    /**
     * Versione per ArraySchema: legge le `parentDeps` dai campi
     * dell'`innerType` (ObjectSchema) e le propaga all'array stesso.
     *
     * Questo permette al parent dell'array di ordinare correttamente
     * il topological sort, evitando che i campi con `parentWhen`
     * dentro l'array vengano risolti prima delle loro dipendenze.
     */
    addChildrenParentDeps(): this;
  }
}

/**
 * Estrae le parentDeps dai figli, le strippa di un livello `^`
 * e le aggiunge a `deps` (per il topological sort di Yup).
 *
 * Le deps che dopo lo strip mantengono il prefisso `^` vengono
 * anche salvate in `meta[ParentDepsKey]` per consentire la
 * propagazione gerarchica: ogni livello (Object → Array → Object
 * sezione) può leggere le deps del livello inferiore e propagarle
 * ulteriormente strippando un altro `^`.
 */
function addParentDepsFromFields<T extends yup.Schema>(
  schema: T,
  children: (yup.ISchema<any> | yup.Reference)[],
): T {
  const deps = children.flatMap(field => {
    if (yup.Reference.isRef(field)) return [];
    const meta = getSchemaMeta(field);
    const parentDeps = meta?.[ParentDepsKey] ?? [];
    return (
      parentDeps.map((dep: string) => dep?.split('.').slice(1).join('.')) ?? []
    );
  });

  if (deps.length === 0) return schema;

  // Aggiungiamo le dipendenze direttamente senza creare una Condition identità
  const next = schema.clone() as any;
  for (const dep of deps) {
    if (!next.deps.includes(dep)) {
      next.deps.push(dep);
    }

    // Propagazione gerarchica: se la dep ha ancora `^`, deve essere
    // visibile al livello superiore tramite meta[ParentDepsKey].
    if (dep.startsWith('^')) {
      if (!next.spec.meta) next.spec.meta = {};
      if (!next.spec.meta[ParentDepsKey]) next.spec.meta[ParentDepsKey] = [];
      if (!next.spec.meta[ParentDepsKey].includes(dep)) {
        next.spec.meta[ParentDepsKey].push(dep);
      }
    }
  }
  return next as T;
}

yup.addMethod<yup.ObjectSchema<YupAnyObject, YupAnyObject>>(
  yup.object,
  'addChildrenParentDeps',
  function (this: yup.ObjectSchema<YupAnyObject, YupAnyObject>) {
    return addParentDepsFromFields(this, Object.values(this.fields));
  },
);

yup.addMethod<yup.ArraySchema<any, any>>(
  yup.array,
  'addChildrenParentDeps',
  function (this: yup.ArraySchema<any, any>) {
    const innerType = (this as any).innerType;
    if (!innerType?.fields) return this;

    return addParentDepsFromFields(this, Object.values(innerType.fields));
  },
);

function getSchemaMeta(schema: yup.ISchema<any>) {
  if ((schema as any).spec?.meta != null) {
    return (schema as any).spec.meta as Record<string, any>;
  }
}
